// src/components/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as authApi from '@api/auth';

const ForgotPassword = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await authApi.requestPasswordReset(email);
      setMessage(data.message);
    } catch (err) {
      setError(err.status ? err.message : 'Network error');    
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '32px 24px' }}>
      <h2 style={{ marginBottom: 8, fontSize: 24, color: 'var(--text-primary)' }}>Reset your password</h2>
      {message ? (
        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>{message}</p>
      ) : (
        <form onSubmit={handleSubmit}>
          <p style={{ marginBottom: 24, color: 'var(--text-secondary)' }}>Enter your email and we will send you a reset link.</p>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%', padding: '12px 16px', marginBottom: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box' }}
          />
          <button type="submit" disabled={loading} style={{ width: '100%', padding: 12, background: 'var(--accent)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 600 }}>
            {loading ? 'Sending...' : 'Send reset link'}
          </button>
          {error && <p style={{ marginTop: 12, color: 'var(--live-red)', fontSize: 14 }}>{error}</p>}
        </form>
      )}
      <button type="button" onClick={onBack} style={{ marginTop: 20, background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', fontSize: 14 }}>
        Back to login
      </button>
    </div>
  );
};

const Login = ({ onSwitchToRegister, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);   // <-- new state
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login } = useAuth();

  // --- Load saved email on mount ---
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await authApi.authenticate(email, password, rememberMe);

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      login(data.token, data.email);
      if (onSuccess) onSuccess(data.token, data.email);

      // // --- Persist email if "Remember Me" is checked ---
      // if (rememberMe) {
      //   localStorage.setItem('rememberedEmail', email);
      // } else {
      //   localStorage.removeItem('rememberedEmail');
      // }

      // --- Log the user in ---
      // login(data.token, data.email);
      // if (onSuccess) onSuccess(data.token, data.email);
    } catch (err) {
      setError(err.status ? err.message : 'Network error');
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        width: '100%',
        padding: '32px 24px',
        background: 'transparent',
        boxSizing: 'border-box',
      }}
    >
      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 340 }}>
        <h2 style={{ marginBottom: 8, fontSize: 24, color: 'var(--text-primary)' }}>Welcome Back</h2>
        <p style={{ marginBottom: 24, color: 'var(--text-secondary)' }}>Log in to your account</p>

        {/* Email Input */}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '12px 16px',
            marginBottom: 12,
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            fontSize: 14,
            outline: 'none',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
          onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
        />

        {/* Password Input */}
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '12px 16px',
            marginBottom: 12,
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--bg-elevated)',
            color: 'var(--text-primary)',
            fontSize: 14,
            outline: 'none',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => (e.target.style.borderColor = 'var(--accent)')}
          onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
        />

        {/* Remember the email address for the next login. */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <input
            type="checkbox"
            id="rememberMe"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            style={{
              marginRight: 8,
              width: 16,
              height: 16,
              accentColor: 'var(--accent)',
              cursor: 'pointer',
            }}
          />
          <label htmlFor="rememberMe" style={{ color: 'var(--text-secondary)', fontSize: 14, cursor: 'pointer' }}>
            Remember email
          </label>
        </div>

        {/* Login Button */}
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '12px',
            background: 'var(--accent)',
            color: 'var(--text-on-accent)',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {loading ? 'Logging in...' : 'Log In'}
        </button>

        {error && (
          <p style={{ marginTop: 12, color: 'var(--live-red)', fontSize: 14 }}>{error}</p>
        )}

        <button
          type="button"
          onClick={() => setShowForgotPassword(true)}
          style={{ marginTop: 16, background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', fontSize: 14 }}
        >
          Forgot password?
        </button>

        <p style={{ marginTop: 20, fontSize: 14, textAlign: 'center', color: 'var(--text-secondary)' }}>
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 14,
            }}
          >
            Register
          </button>
        </p>
      </form>
    </div>
  );
};

export default Login;